import type { Card, Field, Tab } from './library.types'
import { LibraryFilterCardsBy, LibrarySortCardsBy } from './library.enums'
import { testStringForSpecialCharactersOnly } from '@/utils/regex.utils'
import { DateTime } from 'luxon'
import { toRaw, type DeepReadonly, type UnwrapNestedRefs } from 'vue'

/**
 * True when the value contains at least one non-whitespace character.
 */
export function hasNonWhitespaceContent(value: string): boolean {
  return value.trim().length > 0
}

/**
 * Search card title, description, and fields for a search term.
 */
export function matchesSearch(
  title: string | null,
  description: string | null,
  fields: Field[] = [],
  searchTerm: string | null
): boolean {
  const lowercaseSearchTerm = searchTerm?.toLowerCase()

  if (lowercaseSearchTerm && lowercaseSearchTerm !== '') {
    if (title?.toLowerCase().includes(lowercaseSearchTerm)) {
      return true
    }

    if (description?.toLowerCase().includes(lowercaseSearchTerm)) {
      return true
    }

    if (
      fields.find((f) =>
        f.content?.toLowerCase().includes(lowercaseSearchTerm ?? '')
      )
    ) {
      return true
    }

    return false
  }

  return true
}

/**
 * Filter cards based on search term and filter type.
 * Returns a new array (does not mutate input).
 */
export function filterCards(
  cards: Card[],
  searchTerm: string | null,
  filterType: LibraryFilterCardsBy | number,
  currentUserId: number
): Card[] {
  if (testStringForSpecialCharactersOnly(searchTerm ?? '')) return []

  const matches = (c: Card) =>
    matchesSearch(c.title ?? null, c.description ?? null, c.fields, searchTerm)

  switch (filterType) {
    case LibraryFilterCardsBy.Default:
      return cards.filter((c) => matches(c))
    case LibraryFilterCardsBy.Sharing:
      return cards.filter(
        (c) => c.userId === currentUserId && c.shared === true && matches(c)
      )
    case LibraryFilterCardsBy.Private:
      return cards.filter((c) => c.shared === false && matches(c))
    case LibraryFilterCardsBy.My:
      return cards.filter((c) => c.userId === currentUserId && matches(c))
    case LibraryFilterCardsBy.CoParents:
      return cards.filter(
        (c) => c.shared === true && c.userId !== currentUserId && matches(c)
      )
    default:
      return cards.filter((c) => matches(c))
  }
}

/**
 * Sort cards by the specified sort type.
 * Returns a new array (does not mutate input).
 */
export function sortCards(
  cards: Card[],
  sortType: LibrarySortCardsBy | number | '' | null
): typeof cards {
  if (sortType == null || sortType === '') {
    return cards
  }

  const sorted = [...cards]
  const fallbackDate = DateTime.now()

  switch (sortType) {
    case LibrarySortCardsBy.Default:
    case LibrarySortCardsBy.DateDesc:
      sorted.sort((a, b) => {
        const dateA = a.updatedWhen ?? a.createdWhen ?? fallbackDate
        const dateB = b.updatedWhen ?? b.createdWhen ?? fallbackDate
        return dateA > dateB ? -1 : dateA < dateB ? 1 : 0
      })
      break
    case LibrarySortCardsBy.DateAsc:
      sorted.sort((a, b) => {
        const dateA = a.updatedWhen ?? a.createdWhen ?? fallbackDate
        const dateB = b.updatedWhen ?? b.createdWhen ?? fallbackDate
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0
      })
      break
    case LibrarySortCardsBy.TitleAsc:
      sorted.sort((a, b) => {
        const titleA = (a.title ?? '').toUpperCase()
        const titleB = (b.title ?? '').toUpperCase()
        return titleA < titleB ? -1 : titleA > titleB ? 1 : 0
      })
      break
    case LibrarySortCardsBy.TitleDesc:
      sorted.sort((a, b) => {
        const titleA = (a.title ?? '').toUpperCase()
        const titleB = (b.title ?? '').toUpperCase()
        return titleA > titleB ? -1 : titleA < titleB ? 1 : 0
      })
      break
    default:
      sorted.sort((a, b) => {
        const dateA = a.updatedWhen ?? a.createdWhen ?? fallbackDate
        const dateB = b.updatedWhen ?? b.createdWhen ?? fallbackDate
        return dateA > dateB ? -1 : dateA < dateB ? 1 : 0
      })
  }

  return sorted
}

export function createTab(overrides: Partial<Tab> = {}): Tab {
  return {
    itemId: 0,
    name: '',
    userId: 0,
    caseId: 0,
    createdWhen: DateTime.now(),
    cardCount: 0,
    ...overrides
  }
}

export function createCard(overrides: Partial<Card> = {}): Card {
  return {
    itemId: 0,
    tabId: 0,
    tabName: '',
    shared: false,
    title: '',
    userId: 0,
    createdWhen: DateTime.now(),
    fields: [],
    ...overrides
  }
}

export function convertReadonlyCardToRaw(
  card: DeepReadonly<UnwrapNestedRefs<Card>>
): Card {
  const rawCard = toRaw(card)

  // * toRaw returns the underlying object, need to create a copy
  return {
    ...rawCard,
    createdWhen: rawCard.createdWhen.set({}),
    updatedWhen: rawCard.updatedWhen?.set({}),
    viewedWhen: rawCard.viewedWhen?.set({}),
    fields: rawCard.fields?.map((field) => ({ ...toRaw(field) }))
  }
}
